import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Eye, EyeOff, Lock, Mail, ShieldCheck, X } from 'lucide-react';
import bgImage from '../assets/bg_image.jpg';
import icon from '../assets/icon.png';
import { useAppSetting } from '../context/AppSettingContext';

function LoginAdmin({ setUser }) {
  const { appSetting } = useAppSetting();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    setFadeIn(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/admin/login`,
        { email, password },
        { withCredentials: true }
      );

      if (setUser && typeof setUser === 'function') {
        setUser(res.data.user);
      }

      navigate('/dashboard');
    } catch (err) {
      setError(`Login gagal. ${err.response?.data?.message || 'Silakan coba lagi.'}`);
    }
  };

  return (
    <div className="tw min-h-screen w-full bg-slate-950">
      <div className="flex min-h-screen">
        <section
          className="relative hidden w-[58%] overflow-hidden lg:block"
          style={{ backgroundImage: `url(${appSetting.background_image ?? bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-900/75 to-teal-900/70" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(45,212,191,0.28),transparent_42%),radial-gradient(circle_at_78%_30%,rgba(14,165,233,0.22),transparent_40%)]" />

          <div className="relative z-10 flex h-full flex-col justify-between p-10 text-white">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs uppercase tracking-[0.18em]">
              <ShieldCheck size={14} />
              Admin Access
            </div>

            <div className="max-w-xl">
              <h2 className="text-4xl font-semibold leading-tight">Panel kontrol untuk mengelola sistem dengan aman dan terpusat.</h2>
              <p className="mt-4 text-sm text-slate-200/90">
                Pantau data pengguna, dokumen, transaksi, dan pengaturan aplikasi dalam satu dashboard admin.
              </p>
            </div>

            <div className="text-xs text-slate-300">© 2026 {appSetting.nama_aplikasi ?? 'Admin Portal'}</div>
          </div>
        </section>

        <section className={`flex w-full items-center justify-center bg-slate-100 p-6 transition-all duration-700 lg:w-[42%] ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl shadow-slate-300/50 sm:p-8">
            <div className="mb-8 text-center">
              <img src={appSetting.logo_image ?? icon} alt="Logo" className="mx-auto mb-4 h-auto w-28" />
              <h1 className="text-2xl font-semibold text-slate-900">{appSetting.nama_aplikasi ?? '-'}</h1>
              <p className="mt-1 text-sm text-slate-500">Masuk sebagai admin untuk melanjutkan.</p>
              <div className="mx-auto mt-3 h-1.5 w-20 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Email</label>
                <div className="flex items-center rounded-xl border border-slate-300 px-3 focus-within:border-cyan-500 focus-within:ring-2 focus-within:ring-cyan-500/20">
                  <Mail size={16} className="shrink-0 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError('');
                    }}
                    required
                    placeholder="admin@email.com"
                    className="w-full rounded-xl bg-transparent px-2 py-2.5 text-sm outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
                <div className="flex items-center rounded-xl border border-slate-300 px-3 focus-within:border-cyan-500 focus-within:ring-2 focus-within:ring-cyan-500/20">
                  <Lock size={16} className="shrink-0 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError('');
                    }}
                    required
                    placeholder="Masukkan password"
                    className="w-full bg-transparent px-2 py-2.5 text-sm outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                    aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error ? (
                <div className="rounded-xl border border-red-200 bg-gradient-to-br from-red-50 to-rose-50 p-3 text-sm text-red-700 shadow-sm">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-500" />
                    <div className="flex-1">
                      <p className="font-semibold text-red-700">Login gagal</p>
                      <p className="mt-0.5 text-red-600">{error.replace(/^Login gagal\.\s*/, '')}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setError('')}
                      className="rounded-md p-1 text-red-400 hover:bg-red-100 hover:text-red-600"
                      aria-label="Tutup notifikasi error"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ) : null}

              <button
                type="submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Login Admin
                <ArrowRight size={16} />
              </button>
            </form>

            <p className="mt-6 text-center text-xs text-slate-400">
              Halaman ini khusus administrator. Aktivitas login dapat dicatat untuk keamanan sistem.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default LoginAdmin;
